import React, { useState, useEffect } from 'react';
import ClariMeLogo from './ClariMeLogo';
import { navigateTo } from '../hooks/useHashRouter';

// ── Icons ───────────────────────────────────────────────────────────

const MicIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 10v2a7 7 0 01-14 0v-2M12 19v4m-4 0h8" />
  </svg>
);

const DocIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const UsersIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const ArrowRightIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

// ── Welcome Screen ──────────────────────────────────────────────────

export default function PreVisitOnboarding({ auth }) {
  const firstName = auth?.user?.firstName || '';
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleGetStarted = () => {
    markOnboardingComplete();
    navigateTo('/app');
  };

  return (
    <div className="fixed inset-0 flex flex-col bg-white" style={{ height: '100dvh' }}>
      {/* Header */}
      <div className="flex-shrink-0 px-6" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="pt-3 pb-2">
          <ClariMeLogo size="small" variant="light" />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col justify-center px-6 min-h-0">
        <div className={`max-w-sm mx-auto w-full transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>

          {/* Greeting */}
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            {firstName ? `Hi ${firstName},` : 'Hi there,'}
          </h1>
          <p className="text-lg text-slate-500 mb-10 leading-relaxed">
            We'll help you remember everything from your doctor visits, and keep your family in the loop.
          </p>

          {/* How it works — 3 steps */}
          <div className="space-y-5 mb-12">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl flex items-center justify-center flex-shrink-0 border border-teal-200/50">
                <MicIcon className="w-5 h-5 text-teal-600" />
              </div>
              <div className="pt-0.5">
                <h3 className="text-base font-semibold text-slate-900">Record your visit</h3>
                <p className="text-sm text-slate-500 leading-relaxed mt-0.5">
                  Tap Record when your appointment starts. We'll listen in the background.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-11 h-11 bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-xl flex items-center justify-center flex-shrink-0 border border-cyan-200/50">
                <DocIcon className="w-5 h-5 text-cyan-600" />
              </div>
              <div className="pt-0.5">
                <h3 className="text-base font-semibold text-slate-900">Get a clear summary</h3>
                <p className="text-sm text-slate-500 leading-relaxed mt-0.5">
                  Key points, medications, and follow-ups, all in plain language.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-11 h-11 bg-gradient-to-br from-violet-50 to-violet-100 rounded-xl flex items-center justify-center flex-shrink-0 border border-violet-200/50">
                <UsersIcon className="w-5 h-5 text-violet-600" />
              </div>
              <div className="pt-0.5">
                <h3 className="text-base font-semibold text-slate-900">Share with family</h3>
                <p className="text-sm text-slate-500 leading-relaxed mt-0.5">
                  Send summaries to your family by text. No app needed on their end.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <button
            onClick={handleGetStarted}
            className="w-full py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-lg font-semibold rounded-2xl shadow-lg shadow-teal-500/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
          >
            Get Started
            <ArrowRightIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Bottom safe area */}
      <div className="flex-shrink-0 pb-6" />
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

const ONBOARDING_KEY = 'clarime_onboarding_complete';

export function markOnboardingComplete() {
  localStorage.setItem(ONBOARDING_KEY, 'true');
}

export function hasCompletedOnboarding() {
  return localStorage.getItem(ONBOARDING_KEY) === 'true';
}
